import { plausibleHistoryInstantMs } from "./historyDateUtils";

/** Local facility hours: matches "after 7pm / before 7am" copy in reports. */
export function getStartHourBucketLocal(d) {
  const date = d ? new Date(d) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  const h = date.getHours();
  if (h >= 0 && h < 7) return "Late night (00-07)";
  if (h >= 7 && h < 12) return "Morning (07-12)";
  if (h >= 12 && h < 17) return "Afternoon (12-17)";
  if (h >= 17 && h < 19) return "Evening (17-19)";
  return "Night (19-24)";
}

function isCompletedRow(row) {
  return row && row.status === "Completed";
}

function roomKey(row) {
  return String((row.subLocation || row.map_name || "").trim() || "Unknown");
}

/** Same ordering as start-time buckets elsewhere on this row. */
function rowStartForBucket(row) {
  return row.disinfectionStartTime || row.date || row.createdAt;
}

function rowDayKey(row) {
  const d = rowStartForBucket(row);
  if (d == null || d === "") return null;
  const t = new Date(d).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(d).toDateString();
}

function uvcSecondsForRow(row) {
  return Number(row.uvLightUsageInSeconds ?? row.disinfectionTimeTakenSeconds ?? 0);
}

/**
 * Trial window from all rows with plausible instants; table metrics from Completed rows only.
 * @param {object[]} history
 * @param {{ instantFieldOrder: string[] }} options
 */
export function buildOperationalSummaryMetrics(history, { instantFieldOrder }) {
  const empty = {
    trialStart: null,
    trialEnd: null,
    completedCount: 0,
    totalUvcHoursCompleted: 0,
    activeDaysCompleted: 0,
    avgUniqueRoomsPerActiveDay: null,
    peakUniqueRooms: null,
    peakUniqueRoomsDate: null,
    nightShareCompletedPct: 0,
  };

  if (!Array.isArray(history) || history.length === 0) return empty;

  const sortedDates = history
    .map((r) => plausibleHistoryInstantMs(r, instantFieldOrder))
    .filter((t) => !Number.isNaN(t));
  const trialStart = sortedDates.length ? new Date(Math.min(...sortedDates)) : null;
  const trialEnd = sortedDates.length ? new Date(Math.max(...sortedDates)) : null;

  const completed = history.filter(isCompletedRow);
  const completedCount = completed.length;

  let totalUvcSecCompleted = 0;
  let nightCompleted = 0;
  /** @type {Map<string, { rooms: Set<string>, repMs: number }>} */
  const dayMap = new Map();

  for (const row of completed) {
    totalUvcSecCompleted += uvcSecondsForRow(row);

    const bucket = getStartHourBucketLocal(rowStartForBucket(row));
    if (bucket === "Night (19-24)" || bucket === "Late night (00-07)") nightCompleted += 1;

    const dk = rowDayKey(row);
    if (!dk) continue;
    const rm = roomKey(row);
    const dRaw = rowStartForBucket(row);
    const repMs = new Date(dRaw).getTime();
    if (!dayMap.has(dk)) {
      dayMap.set(dk, { rooms: new Set([rm]), repMs });
    } else {
      const meta = dayMap.get(dk);
      meta.rooms.add(rm);
      if (!Number.isNaN(repMs) && repMs < meta.repMs) meta.repMs = repMs;
    }
  }

  const activeDaysCompleted = dayMap.size;
  const roomCounts = [...dayMap.values()].map((m) => m.rooms.size);
  const avgUniqueRoomsPerActiveDay =
    roomCounts.length > 0
      ? Math.round((roomCounts.reduce((a, b) => a + b, 0) / roomCounts.length) * 100) / 100
      : null;

  let peakUniqueRooms = null;
  let peakUniqueRoomsDate = null;
  for (const [, meta] of dayMap) {
    const n = meta.rooms.size;
    if (peakUniqueRooms == null || n > peakUniqueRooms) {
      peakUniqueRooms = n;
      peakUniqueRoomsDate = Number.isNaN(meta.repMs) ? null : new Date(meta.repMs);
    }
  }

  const nightShareCompletedPct =
    completedCount > 0 ? Math.round((nightCompleted / completedCount) * 1000) / 10 : 0;

  return {
    trialStart,
    trialEnd,
    completedCount,
    totalUvcHoursCompleted: Math.round((totalUvcSecCompleted / 3600) * 10) / 10,
    activeDaysCompleted,
    avgUniqueRoomsPerActiveDay,
    peakUniqueRooms,
    peakUniqueRoomsDate,
    nightShareCompletedPct,
  };
}
